// STUDIO-BLUETOOTH-REMOTE-INPUT-DIAG-1/-POINTER-DIAG-2 · 리모컨 입력 진단 순수 함수 (메모리 전용 · 서버 전송 없음).
// 키코드·좌표는 하드코딩하지 않는다. RISKY 는 브라우저·시스템 동작을 유발할 수 있는 일반 키(주의 표시용)일 뿐.
export const MAX_RECORDS = 300;
const RISKY = new Set([
  'BrowserBack', 'BrowserForward', 'BrowserHome', 'BrowserRefresh', 'GoBack', 'Escape', 'Backspace', 'Tab',
  'F5', 'AudioVolumeUp', 'AudioVolumeDown', 'AudioVolumeMute', 'VolumeUp', 'VolumeDown', 'MediaPlayPause',
  'MediaTrackNext', 'MediaTrackPrevious', 'Camera', 'Power', 'ContextMenu', 'Meta', 'OS',
]);
export const SEGMENTS = [
  { id: 'none', label: '구간 없음' },
  { id: 'up', label: '위' }, { id: 'down', label: '아래' }, { id: 'left', label: '왼쪽' }, { id: 'right', label: '오른쪽' },
  { id: 'center', label: '중앙' }, { id: 'shutter', label: '하단 셔터' },
  { id: 'off_finger', label: '연결 해제 · 손가락 터치' }, { id: 'on_finger', label: '연결 중 · 손가락 터치' },
];
export const segLabel = (id) => (SEGMENTS.find((s) => s.id === id) || SEGMENTS[0]).label;

export const isTextTarget = (t) => !!t && (t.isContentEditable
  || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));

export const isRisky = (key, code) => RISKY.has(key) || RISKY.has(code);

export const describeTarget = (t) => {
  if (!t || !t.tagName) return '-';
  const tid = t.dataset && t.dataset.testid;
  return `${t.tagName.toLowerCase()}${tid ? `[${tid}]` : t.id ? `#${t.id}` : ''}`;
};

const base = (e, seq, now) => ({
  seq, t: now, type: e.type,
  mods: { ctrl: !!e.ctrlKey, alt: !!e.altKey, shift: !!e.shiftKey, meta: !!e.metaKey },
  focus: typeof document !== 'undefined' && document.hasFocus(),
  target: describeTarget(e.target),
});

export const fromKeyEvent = (e, seq, now, downAt) => {
  const text = isTextTarget(e.target);
  return {
    ...base(e, seq, now),
    key: text ? '(입력창 · 미기록)' : e.key,
    code: text ? '(입력창 · 미기록)' : e.code,
    keyCode: text ? null : (e.keyCode || null),
    repeat: !!e.repeat,
    heldMs: e.type === 'keyup' && downAt != null ? Math.round(now - downAt) : null,
    risky: !text && isRisky(e.key, e.code),
  };
};

const r1 = (n) => (typeof n === 'number' ? Math.round(n * 10) / 10 : null);

// 화면·브라우저 크기 스냅샷 (주소창·회전·확대에 따른 좌표 변화 비교용).
export const viewportSnapshot = () => {
  if (typeof window === 'undefined') return null;
  const vv = window.visualViewport;
  return {
    iw: window.innerWidth, ih: window.innerHeight, sw: window.screen?.width, sh: window.screen?.height,
    dpr: window.devicePixelRatio, orient: window.screen?.orientation?.type || '-',
    vvw: vv ? r1(vv.width) : null, vvh: vv ? r1(vv.height) : null, vvTop: vv ? r1(vv.offsetTop) : null,
    vvLeft: vv ? r1(vv.offsetLeft) : null, vvScale: vv ? r1(vv.scale) : null,
  };
};
const ratio = (v, total) => (typeof v === 'number' && total ? Math.round((v / total) * 1000) / 1000 : null);

export const fromOtherEvent = (e, seq, now) => {
  const isPtr = e.pointerType !== undefined || e.type.startsWith('pointer');
  const rec = {
    ...base(e, seq, now),
    key: e.type === 'wheel' ? `dx=${Math.round(e.deltaX || 0)} dy=${Math.round(e.deltaY || 0)}` : (e.pointerType || ''),
    code: e.button !== undefined && e.type !== 'wheel' ? `button=${e.button}` : '',
    repeat: false, heldMs: null, risky: e.type === 'popstate',
  };
  if (e.clientX !== undefined) Object.assign(rec, { x: r1(e.clientX), y: r1(e.clientY) });
  if (e.screenX !== undefined) {
    const sw = typeof window !== 'undefined' ? window.screen?.width : null;
    const sh = typeof window !== 'undefined' ? window.screen?.height : null;
    Object.assign(rec, { sx: r1(e.screenX), sy: r1(e.screenY), nx: ratio(e.screenX, sw), ny: ratio(e.screenY, sh) });
  }
  if (['pointerdown', 'pointerup', 'pointercancel'].includes(e.type)) rec.vp = viewportSnapshot();
  if (e.type === 'pointercancel') rec.note = '취소 이벤트 좌표는 브라우저가 0 으로 보낼 수 있음(의미 없음)';
  if (e.button !== undefined) Object.assign(rec, { button: e.button, buttons: e.buttons });
  if (isPtr) {
    Object.assign(rec, {
      pointerType: e.pointerType || '', pointerId: e.pointerId ?? null, pressure: r1(e.pressure),
      isPrimary: e.isPrimary ?? null, width: r1(e.width), height: r1(e.height),
    });
  }
  return rec;
};

// pointerdown → (move) → pointerup/cancel 한 번의 동작 요약. 방향키 등이 '스와이프'로 들어오는지 확인용.
export const gestureRecord = (g, upEvt, seq, now) => {
  // pointercancel 좌표(0,0)는 의미 없음 → 마지막 이동 좌표 사용.
  const useLast = upEvt.type === 'pointercancel' || upEvt.clientX == null;
  const ex = useLast ? g.lastX : upEvt.clientX;
  const ey = useLast ? g.lastY : upEvt.clientY;
  const dx = r1(ex - g.x);
  const dy = r1(ey - g.y);
  const ax = Math.abs(dx); const ay = Math.abs(dy);
  const dir = Math.max(ax, ay) < 10 ? '탭' : ax > ay ? (dx > 0 ? '→ 오른쪽' : '← 왼쪽') : (dy > 0 ? '↓ 아래' : '↑ 위');
  return {
    seq, t: now, type: upEvt.type === 'pointercancel' ? 'gesture(cancel)' : 'gesture',
    key: g.pointerType, code: dir, repeat: false, heldMs: Math.round(now - g.t), risky: false,
    mods: { ctrl: false, alt: false, shift: false, meta: false }, focus: typeof document !== 'undefined' && document.hasFocus(),
    target: g.target, pointerType: g.pointerType, pointerId: g.pointerId,
    x: g.x, y: g.y, x2: r1(ex), y2: r1(ey), dx, dy, moves: g.moves, dir, sx: g.sx, sy: g.sy, endFrom: useLast ? 'last-move' : 'up',
  };
};

export const withTiming = (rec, prevT, startT) => ({
  ...rec, gapMs: prevT == null ? null : Math.round(rec.t - prevT), atMs: Math.round(rec.t - startT),
});

export const pushRecord = (list, rec) => [rec, ...list].slice(0, MAX_RECORDS);

export const summarize = (list) => {
  const m = new Map();
  [...list].reverse().forEach((r) => {
    if (r.type !== 'keydown' && r.type !== 'keyup') return;
    const id = `${r.key}|${r.code}`;
    const s = m.get(id) || { key: r.key, code: r.code, keyCode: r.keyCode, down: 0, repeat: 0, up: 0, maxHeldMs: 0, risky: r.risky };
    if (r.type === 'keydown') { s.down += 1; if (r.repeat) s.repeat += 1; }
    if (r.type === 'keyup') { s.up += 1; s.maxHeldMs = Math.max(s.maxHeldMs, r.heldMs || 0); }
    m.set(id, s);
  });
  return [...m.values()];
};

// 테스트 구간별 비교: 이벤트 종류 수 · 포인터 종류 · 좌표 범위 · 동작 방향 · 평균 간격.
export const summarizeSegments = (list) => {
  const m = new Map();
  [...list].reverse().forEach((r) => {
    const id = r.seg || 'none';
    const s = m.get(id) || { seg: id, label: segLabel(id), count: 0, types: {}, pointerTypes: {}, buttons: {}, dirs: {}, xs: [], ys: [], gaps: [], pressures: {}, keys: {} };
    s.count += 1;
    s.types[r.type] = (s.types[r.type] || 0) + 1;
    if (r.pointerType) s.pointerTypes[r.pointerType] = (s.pointerTypes[r.pointerType] || 0) + 1;
    if (r.type === 'pointerdown') {
      s.buttons[`${r.button}/${r.buttons}`] = (s.buttons[`${r.button}/${r.buttons}`] || 0) + 1;
      s.pressures[String(r.pressure)] = (s.pressures[String(r.pressure)] || 0) + 1;
      if (r.x != null) { s.xs.push(r.x); s.ys.push(r.y); }
    }
    if (r.dir) s.dirs[r.dir] = (s.dirs[r.dir] || 0) + 1;
    if (r.type === 'keydown') s.keys[`${r.key}/${r.code}`] = (s.keys[`${r.key}/${r.code}`] || 0) + 1;
    if (r.gapMs != null) s.gaps.push(r.gapMs);
    m.set(id, s);
  });
  return [...m.values()].map((s) => ({
    ...s,
    xRange: s.xs.length ? [Math.min(...s.xs), Math.max(...s.xs)] : null,
    yRange: s.ys.length ? [Math.min(...s.ys), Math.max(...s.ys)] : null,
    avgGapMs: s.gaps.length ? Math.round(s.gaps.reduce((a, b) => a + b, 0) / s.gaps.length) : null,
  }));
};

const mods = (m) => ['ctrl', 'alt', 'shift', 'meta'].filter((k) => m[k]).join('+') || '-';
const kv = (o) => Object.entries(o).map(([k, v]) => `${k}:${v}`).join(', ') || '-';

export const fmtPointer = (r) => {
  const scr = r.sx != null ? ` screen=(${r.sx},${r.sy})${r.nx != null ? ` ratio=(${r.nx},${r.ny})` : ''}` : '';
  const vp = r.vp ? ` vp=${r.vp.iw}x${r.vp.ih} scr=${r.vp.sw}x${r.vp.sh} dpr=${r.vp.dpr} vv=${r.vp.vvw}x${r.vp.vvh}@${r.vp.vvLeft},${r.vp.vvTop} zoom=${r.vp.vvScale} ${r.vp.orient}` : '';
  if (r.dir) return `(${r.x},${r.y})→(${r.x2},${r.y2}) dx=${r.dx} dy=${r.dy} ${r.dir} moves=${r.moves} 끝=${r.endFrom}${scr}`;
  if (r.pointerType === undefined) return r.x != null ? `xy=(${r.x},${r.y})${scr} button=${r.button} buttons=${r.buttons}` : '';
  return `type=${r.pointerType} id=${r.pointerId} button=${r.button} buttons=${r.buttons} xy=(${r.x},${r.y})${scr} pressure=${r.pressure} primary=${r.isPrimary}${vp}`;
};

export const toText = (list, env = {}) => [
  '[LIVEMOA 리모컨 입력 진단]',
  `환경: ${env.ua || '-'}`,
  `화면: ${env.screen || '-'}`,
  `포인터 상태: ${env.pen || '-'}`,
  `기록 수: ${list.length}`,
  '',
  '## 구간별 비교',
  ...summarizeSegments(list).map((s) => `[${s.label}] ${s.count}건 · 종류 ${kv(s.types)} · 포인터 ${kv(s.pointerTypes)} · button/buttons ${kv(s.buttons)} · pressure ${kv(s.pressures)} · 방향 ${kv(s.dirs)} · 키 ${kv(s.keys)} · x ${s.xRange ? s.xRange.join('~') : '-'} · y ${s.yRange ? s.yRange.join('~') : '-'} · 평균 간격 ${s.avgGapMs ?? '-'}ms`),
  '',
  '## 키 요약 (key | code | keyCode | keydown | repeat | keyup | 최장 누름 ms)',
  ...summarize(list).map((s) => `${s.key} | ${s.code} | ${s.keyCode ?? '-'} | ${s.down} | ${s.repeat} | ${s.up} | ${s.maxHeldMs}${s.risky ? ' | 주의' : ''}`),
  '',
  '## 입력 순서 (오래된 순)',
  ...[...list].reverse().map((r) => `#${r.seq} [${segLabel(r.seg)}] +${r.atMs ?? '-'}ms (간격 ${r.gapMs ?? '-'}ms) ${r.type} ${r.pointerType === undefined && !r.dir ? `key=${r.key} code=${r.code} keyCode=${r.keyCode ?? '-'} repeat=${r.repeat} ` : ''}${fmtPointer(r)} mods=${mods(r.mods)}${r.heldMs != null ? ` held=${r.heldMs}ms` : ''} target=${r.target || '-'} focus=${r.focus}${r.risky ? ' 주의' : ''}`),
].join('\n');
